/**
 * Atlas portal route definitions — shared between the React Router config on
 * the frontend and Arc's URL-builder service on the server. The router in
 * `client/src/routes.tsx` consumes every entry via `path: ROUTES.X`, which is
 * the discipline mechanism: a new route only renders if it's added here.
 *
 * `ClientPageTabNames` lives here because `ROUTES` interpolates it into a
 * couple of tab-deep links (CLIENT_PAGE_RISK_PROFILE, ...).
 *
 * Frontend-only utilities that need `react-router-dom` (matchPath helpers,
 * BLANK_LAYOUT_ROUTES) stay in `client/src/constants/routes.ts`.
 */
import escapeRegExp from 'lodash/escapeRegExp'
import { TASKS_MODULE_NAME } from './task'

export enum ClientPageTabNames {
  // "Overview" is the company/CRM overview (timeline, network, AI dossier),
  // matching the prospect and diligence pages. The former "Overview" — the loan
  // account-activity ledger, amendments and term loans — is now "Activity".
  Overview = 'Overview',
  Activity = 'Activity',
  Risk = 'Risk & Compliance',
  Financials = 'Financials',
  Collateral = 'Collateral',
  Receivables = 'Receivables',
  Payable = 'Payables',
  Inventory = 'Inventory',
  BankTransactions = 'Transactions',
  CapTable = 'Cap Table',
}

export const ROUTES = {
  HOMEPAGE: '/',
  MY_TASKS: '/my-tasks',
  CALENDAR: '/calendar',
  MESSAGES: '/messages',
  MESSAGES_CHANNEL: '/messages/:channelId',
  // Homepages. Literal segments so they can never be read as a :channelId.
  MESSAGES_CHANNEL_HOME: '/messages/group/:channelId',
  MESSAGES_PERSON_HOME: '/messages/person/:userId',
  MESSAGES_ARC_HOME: '/messages/arc',
  TASK_PROJECTS: '/projects',
  TASK_PROJECT_PAGE: '/projects/:id',
  CUSTOM_FIELDS: '/custom-fields',
  ATLAS_AI: '/atlas-ai',
  ATLAS_AI_CHAT: '/atlas-ai/chat',
  ATLAS_AI_DOCS: '/atlas-ai/docs',
  ATLAS_AI_MEMORIES: '/atlas-ai/memories',
  ATLAS_AI_PROMPTS: '/atlas-ai/prompts',
  ATLAS_AI_WORKFLOWS: '/atlas-ai/workflows',
  ATLAS_AI_AUTOMATIONS: '/atlas-ai/automations',
  ATLAS_AI_SETTINGS: '/atlas-ai/settings',
  LOGIN: '/login',
  LOGIN_ERROR: '/login-error',
  BBC_SIGN_SUCCESS: '/bbc-sign-success',

  SIGNUP: '/signup',
  CONFIRM_EMAIL: '/confirm-email/:hash',
  COMPANY: '/company',
  CLIENTS: '/clients',
  CLIENT_PAGE: '/clients/:id',
  CLIENT_PAGE_RISK_PROFILE: `/clients/:id/?tab=${encodeURIComponent(ClientPageTabNames.Risk)}`,
  CLIENT_PAGE_FINANCIALS_TAB: `/clients/:id/?tab=${encodeURIComponent(ClientPageTabNames.Financials)}`,
  CLIENT_SUBMIT_REPORTING_PAGE: '/clients/:id/submit-reporting',
  CLIENT_SUBMIT_LEGAL_REPORTING_PAGE: '/clients/:id/submit-legal-reporting',
  RESET_PASSWORD: '/reset-password/:hash',
  PASSWORD_RECOVERY: '/password-recovery',
  PASSWORD_SETUP: '/password-setup/:hash',
  CLIENT_SETUP_CREATE: '/client-setup',
  CLIENT_SETUP: '/client-setup/:id',
  CLIENT_SETUP_LOAN_STRUCTURE_ASSET_BASED: '/client-setup/:id/loan-structure-asset-based',
  CLIENT_SETUP_LOAN_STRUCTURE_TERM: '/client-setup/:id/loan-structure-term',
  CLIENT_SETUP_LOAN_STRUCTURE_OVERADVANCE: '/client-setup/:id/loan-structure-overadvance',
  CLIENT_SETUP_LOAN_STRUCTURE_PARTICIPANTS: '/client-setup/:id/loan-structure-participants',
  CLIENT_SETUP_COLLATERAL_RECEIVABLE: '/client-setup/:id/collateral-receivable',
  CLIENT_SETUP_COLLATERAL_PAYABLE: '/client-setup/:id/collateral-payable',
  CLIENT_SETUP_COLLATERAL_INVENTORY: '/client-setup/:id/collateral-inventory',
  CLIENT_SETUP_ITEMS_TO_MONITOR: '/client-setup/:id/items-to-monitor',
  CLIENT_SETUP_REPORTING: '/client-setup/:id/reporting',
  CLIENT_SETUP_BANK_ACCOUNTS: '/client-setup/:id/bank-accounts',
  CLIENT_SETUP_USERS: '/client-setup/:id/users',
  CLIENT_SETUP_RELATIONSHIP_MANAGEMENT: '/client-setup/:id/relationship-management',
  CLIENT_SETUP_TERMINATION: '/client-setup/:id/termination',
  CLIENT_SETUP_GENERAL: '/client-setup/:id/general',
  CLIENT_SETUP_API_KEYS: '/client-setup/:id/api-keys',
  CLIENT_SETUP_COMMUNICATIONS: '/client-setup/:id/communications',
  CLIENT_SETUP_PHONE_NUMBERS: '/client-setup/:id/phone-numbers',
  EXTERNAL_API_DOCS: '/external-api/docs',
  CLIENT_INTEGRATIONS: '/client-integrations/:id',
  CLIENT_INTAKE_HOME: '/client-intake',
  CLIENT_INTAKE_CLAIM: '/client-intake/:salesforceId/claim',
  CLIENT_INTAKE: '/client-intake/:salesforceId',
  MY_FEEDBACK: '/my-feedback',
  ERROR_CENTER: '/error-center',
  SETTINGS: '/settings',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_SECURITY: '/settings/security',
  CLIENT_SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_DISPLAY: '/settings/display',
  SETTINGS_ACCESS_TOKENS: '/settings/access-tokens',
  PORTFOLIO_SETTINGS: '/portfolio-settings',
  CLIENT_MANAGEMENT: '/client-management',
  CLIENT_REPORTING: '/client-management?tab=Compliance',
  USER_MANAGEMENT: '/user-management',
  TEAMS: '/user-management/teams',
  AUDIT: '/audit-log',
  ACCOUNT_ACTIVITY: '/account-activity',
  SUBMIT_REPORTING: '/submit-reporting',
  ACTIVITY_QUEUE: '/operations',
  ANALYSIS_QUEUE: '/operations?tab=Reporting',
  OPERATIONS_AMENDMENTS: '/operations?tab=Amendments',
  AMENDMENT_ANALYSIS: '/clients/:id/amendment-analysis/:analysisId',
  OUTGOING_WIRE: '/outgoing-wire',
  COLLECTIONS_DASHBOARD: '/collections-dashboard',
  COLLECTIONS_DASHBOARD_OVERALL: '/collections-dashboard/overall',
  COLLECTIONS_DASHBOARD_NON_CLIENT_RELATED_CASH: '/collections-dashboard/non-client-related-cash',
  COLLECTIONS_WIRES: '/collections/wires',
  COLLECTIONS_MID_DAY_WIRES: '/collections/mid-day-wires',
  COLLECTIONS_CHECKS: '/collections/checks',
  COLLECTIONS_CHECK_INVOICE_REVIEW: '/collections/check-invoice-review',
  ENTITY_MANAGEMENT: '/entities',
  ORGANIZATION_PAGE: '/organization/:id',
  CRM_HOME: '/crm',
  CRM_SETTINGS: '/crm/settings',
  CRM_ORGANIZATION_PAGE: '/crm/organizations/:id',
  CRM_COMPANY_PAGE: '/crm/companies/:id',
  CRM_PERSON_PAGE: '/crm/people/:id',
  CRM_CAMPAIGN_PAGE: '/crm/campaigns/:id',
  ENTITY_SETTINGS: '/entity-settings/:id',
  ENTITY_SETTINGS_GENERAL: '/entity-settings/:id/general',
  ENTITY_SETTINGS_RECEIVABLES: '/entity-settings/:id/receivables',
  ENTITY_SETTINGS_PAYABLES: '/entity-settings/:id/payables',
  ENTITY_SETTINGS_ALIAS_MAPPING: '/entity-settings/:id/aliases',
  ENTITY_SETTINGS_RELATED_ENTITIES: '/entity-settings/:id/related-entities',

  BBC_INVENTORY: '/bbc/:id/inventory',
  BBC_COLLATERAL_ELIGIBILITY: '/bbc/:id/collateral-eligibility',
  BBC_COLLATERAL_ELIGIBILITY_RECEIVABLES: '/bbc/:id/collateral-eligibility/receivables',
  BBC_COLLATERAL_ELIGIBILITY_INVENTORY: '/bbc/:id/collateral-eligibility/inventory',
  BBC_AR_INELIGIBILITY: '/bbc/:id/ar-ineligibility',
  BBC_INVENTORY_INELIGIBLE: '/bbc/:id/inventory-ineligible',
  BBC_INVENTORY_INELIGIBLE_ANALYSIS: '/bbc/:id/inventory-ineligible-analysis',
  BBC_ANALYSIS: '/bbc/:id/analysis',
  BBC_SUMMARY: '/bbc/:id/summary',
  BBC_SUMMARY_HISTORICAL: '/bbc/:id/summary/historical',
  BBC_SUMMARY_RECEIVABLES: '/bbc/:id/summary/receivables',
  BBC_SUMMARY_PAYABLES: '/bbc/:id/summary/payables',
  BBC_SUMMARY_INVENTORY: '/bbc/:id/summary/inventory',

  ONGOING_REPORTING: '/ongoing-reporting/:id',
  ONGOING_REPORTING_INCOME_STATEMENT: '/ongoing-reporting/:id/income-statement',
  ONGOING_REPORTING_BALANCE_SHEET: '/ongoing-reporting/:id/balance-sheet',
  ONGOING_REPORTING_SUMMARY: '/ongoing-reporting/:id/summary',
  ONGOING_REPORTING_SALES_BY_SKU: '/ongoing-reporting/sales-by-sku/:id',
  ONGOING_REPORTING_AR_GENERAL_LEDGER: '/ongoing-reporting/ar-general-ledger/:id',
  ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD: '/ongoing-reporting/:id/bank-transactions',
  ONGOING_REPORTING_CAP_TABLE_UPLOAD: '/ongoing-reporting/:id/cap-table',
  ONGOING_REPORTING_INSURANCE_PAGE: '/ongoing-reporting/:id/insurance',
  ONGOING_REPORTING_CERTIFICATES_PAGE: '/ongoing-reporting/:id/certificates',

  PROSPECTS: '/prospects-list',
  PROSPECT_SETUP: '/prospects',
  PROSPECT_PAGE: '/prospects/:id',
  PROSPECT_OVERVIEW_PAGE: '/prospects/:id/overview',
  PROSPECT_APPLICATION_PAGE: '/prospects/:id/application',
  PROSPECT_ANALYSIS_QUEUE: '/prospects/:id/analysis-queue',
  PROSPECT_SUMMARY_PAGE: '/prospects/:id/summary',
  PROSPECT_REPORTING_RECEIVABLES: '/prospects/:id/receivables',
  PROSPECT_REPORTING_PAYABLES: '/prospects/:id/payables',
  PROSPECT_REPORTING_INVENTORY: '/prospects/:id/inventory',
  PROSPECT_REPORTING_INCOME_STATEMENT: '/prospects/:id/income-statement',
  PROSPECT_REPORTING_BALANCE_SHEET: '/prospects/:id/balance-sheet',
  PROSPECT_REPORTING_INCOME_STATEMENT_PROJECTIONS: '/prospects/:id/income-statement-projections',
  PROSPECT_REPORTING_BALANCE_SHEET_PROJECTIONS: '/prospects/:id/balance-sheet-projections',
  PROSPECT_REPORTING_CAP_TABLE: '/prospects/:id/cap-table',
  PROSPECT_SETTINGS_USERS_PAGE: '/prospects/:id/settings/users',
  PROSPECT_MANAGE_TERMS_PAGE: '/prospects/:id/manage-terms',
  PROSPECT_ADD_TERMS_PAGE: '/prospects/:id/manage-terms/add',
  PROSPECT_EDIT_TERMS_PAGE: '/prospects/:id/manage-terms/edit/:termId',

  DUE_DILIGENCE_HOME: '/due-diligence-home',
  DUE_DILIGENCE_SETTINGS: '/due-diligence-settings',
  AMENDMENT_ANALYSIS_SETTINGS: '/amendment-analysis-settings',
  DUE_DILIGENCE: '/due-diligence/:id',
  DUE_DILIGENCE_OVERVIEW: '/due-diligence/:id/overview',
  DUE_DILIGENCE_CLIENT_FINANCIALS_PAGE: '/due-diligence/:id/client-financials',
  DUE_DILIGENCE_ANALYSIS_QUEUE: '/due-diligence/:id/analysis-queue',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_LIST: '/due-diligence/:id/process-documents/:type',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_MAPPING: '/due-diligence/:id/process-documents/:type/mapping',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_FINANCIALS:
    '/due-diligence/:clientId/process-documents/:type/financials/:id',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_BANK_TRANSACTIONS:
    '/due-diligence/:id/process-documents/:type/transaction-mapping',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_GENERAL_LEDGER:
    '/due-diligence/:id/process-documents/:type/ar-general-ledger-mapping',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_INVENTORY:
    '/due-diligence/:id/process-documents/:type/inventory-mapping',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_SALES_BY_SKU:
    '/due-diligence/:id/process-documents/:type/sales-by-sku-mapping',
  DUE_DILIGENCE_PROCESS_DOCUMENTS_CAP_TABLE:
    '/due-diligence/:id/process-documents/:type/cap-table-mapping',
  DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE: '/due-diligence/:id/validate-documents',

  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAGE: '/due-diligence/:id/loan-committee-report',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FINANCIALS_PAGE:
    '/due-diligence/:id/loan-committee-report/financials',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FORECAST_PAGE:
    '/due-diligence/:id/loan-committee-report/forecasts',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_TRANSACTIONS_PAGE:
    '/due-diligence/:id/loan-committee-report/transactions',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_RECEIVABLES_PAGE:
    '/due-diligence/:id/loan-committee-report/receivables',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAYABLES_PAGE:
    '/due-diligence/:id/loan-committee-report/payables',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_INVENTORY_PAGE:
    '/due-diligence/:id/loan-committee-report/inventory',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_CAP_TABLE_PAGE:
    '/due-diligence/:id/loan-committee-report/cap-table',
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FIELD_EXAM_PAGE:
    '/due-diligence/:id/loan-committee-report/field-exam',

  ENTITY_PAGE: '/entity/:id',

  PARTICIPANTS_MANAGEMENT: '/participants',
  PARTICIPANTS_PAGE: '/participants/:id',
  PARTICIPANT_SETTINGS: '/participant/:id/settings',
  MARKET_NEWS: '/market-news',
  MARKET_INTELLIGENCE: '/market-intelligence',
  NEWSFEED_WRONG_MATCH: '/newsfeed/wrong-match/:newsfeedId',

  NOTIFICATIONS: '/notifications',

  FUNDING: '/funding',
  EXPORTS_PAGE: '/exports',
  REPORTS: '/reports',
  REPORT_PAGE: '/reports/:id',

  DOCUMENT_EDITOR: '/due-diligence/:id/document-editor/:fileId',

  CODAT_REDIRECT_PAGE: '/codat-redirect/:companyId',
  QBO_REDIRECT_PAGE: '/qbo-redirect',

  GLOBAL_SEARCH: '/search',
  GLOBAL_ERROR: '/error',

  CLIENT_VISUALIZATIONS: '/client-visualizations',

  APPLICATION_PAGE: '/application',
  DOCUMENTS_PAGE: '/documents',
} as const

export type RouteKey = keyof typeof ROUTES

export interface IRouteMetadata {
  /** Short human name for this route, used wherever a page has to be named in the UI. */
  label: string
  description: string
  category: string
}

/**
 * Human-readable description of every route, surfaced to the AI agent so it
 * can pick the right deep-link key. The TypeScript signature
 * `Record<RouteKey, IRouteMetadata>` forces a description for every entry in
 * `ROUTES` — adding a new route without a label and description will fail compilation.
 */
export const ROUTE_METADATA: Record<RouteKey, IRouteMetadata> = {
  HOMEPAGE: {
    label: 'Home',
    description:
      'Atlas portal home / dashboard. Query params: eventId (a Google event id) opens that meeting on the Today card, with date (YYYY-MM-DD) for a day other than today',
    category: 'Core',
  },
  MY_TASKS: {
    label: 'My tasks',
    description: `${TASKS_MODULE_NAME} (Atlas Tasks) — My Tasks page (task list for current / all internal users). Query param: taskId opens that task's detail panel`,
    category: 'Core',
  },
  CALENDAR: {
    label: 'Calendar',
    description:
      "The user's own Google calendar as a day, 3-day or week grid. Query params: view (day | 3day | week | month), date (YYYY-MM-DD), weekends (true | false), eventId (a Google event id, with its date) opens that meeting",
    category: 'Core',
  },
  MESSAGES: {
    label: 'Messages',
    description:
      'AIM (Atlas IM) — team messaging: groups, threads and direct messages. Query param: panel (search | members | scheduled) opens that side panel',
    category: 'Core',
  },
  MESSAGES_CHANNEL: {
    label: 'Thread',
    description:
      'A single AIM thread or direct message. Query param: messageId jumps to that message, panel (search | members | scheduled) opens that side panel, settings=1 opens the conversation settings',
    category: 'Core',
  },
  MESSAGES_CHANNEL_HOME: {
    label: 'Group home',
    description: "An AIM group's homepage — its threads, tasks, pinned messages and brief",
    category: 'Core',
  },
  MESSAGES_ARC_HOME: {
    label: 'Arc home',
    description: "Arc's AIM homepage — its threads and scheduled automations",
    category: 'Core',
  },
  MESSAGES_PERSON_HOME: {
    label: 'Person home',
    description: "A teammate's AIM homepage — shared threads, tasks, pinned messages and brief",
    category: 'Core',
  },
  TASK_PROJECTS: {
    label: 'Projects',
    description: 'Task projects overview (named containers grouping Atlas tasks)',
    category: 'Core',
  },
  TASK_PROJECT_PAGE: {
    label: 'Project',
    description:
      'Single task project page (section-grouped task list). Query param: taskId opens that task in the detail panel',
    category: 'Core',
  },
  CUSTOM_FIELDS: {
    label: 'Custom fields',
    description: `Custom fields library for ${TASKS_MODULE_NAME} (Atlas Tasks) — reusable task fields`,
    category: 'Core',
  },
  ATLAS_AI: {
    label: 'Arc',
    description: 'Arc / Atlas AI settings and automations',
    category: 'AtlasAI',
  },
  // Kept as a route so old links resolve, but it only redirects now — chatting
  // with Arc happens in AIM. Described accurately because these
  // descriptions are what route-aware AI features read.
  ATLAS_AI_CHAT: {
    label: 'Arc chat',
    description: 'Retired Arc chat page — redirects to AIM, where Arc is a conversation',
    category: 'AtlasAI',
  },
  ATLAS_AI_DOCS: {
    label: 'Arc docs',
    description: 'Arc shared knowledge / docs library',
    category: 'AtlasAI',
  },
  ATLAS_AI_MEMORIES: {
    label: 'Arc memories',
    description: 'Arc agent memory store',
    category: 'AtlasAI',
  },
  ATLAS_AI_PROMPTS: {
    label: 'Arc prompts',
    description: 'Arc user-authored prompts',
    category: 'AtlasAI',
  },
  ATLAS_AI_WORKFLOWS: {
    label: 'Arc workflows',
    description: 'Arc code-backed workflow catalog',
    category: 'AtlasAI',
  },
  ATLAS_AI_AUTOMATIONS: {
    label: 'Arc automations',
    description: 'Arc scheduled automations',
    category: 'AtlasAI',
  },
  ATLAS_AI_SETTINGS: {
    label: 'Arc settings',
    description:
      'Arc settings: the user\'s AI dossier, plus their Arc voice call settings (the voice, accent, and "Cutting in": how easily talking over Arc, or noise, interrupts it)',
    category: 'AtlasAI',
  },
  LOGIN: { label: 'Login', description: 'Login page', category: 'Auth' },
  LOGIN_ERROR: { label: 'Login error', description: 'Login error page', category: 'Auth' },
  BBC_SIGN_SUCCESS: {
    label: 'Signature complete',
    description: 'BBC e-sign confirmation page',
    category: 'BBC',
  },
  SIGNUP: { label: 'Sign up', description: 'Account signup', category: 'Auth' },
  CONFIRM_EMAIL: {
    label: 'Confirm email',
    description: 'Email confirmation page (params: hash from email link)',
    category: 'Auth',
  },
  COMPANY: {
    label: 'Company',
    description: 'Company info page (Dwight Funding)',
    category: 'Core',
  },
  CLIENTS: {
    label: 'Clients',
    description: 'All clients list (portfolio view)',
    category: 'Clients',
  },
  CLIENT_PAGE: {
    label: 'Client',
    description:
      'Client page (Overview tab default). Query params: tab (Overview | Activity | Risk & Compliance | Financials | Collateral | Receivables | Payables | Inventory | Transactions | Cap Table), scrollTo (a section id), noteId (opens that note in the side panel), flagId (opens that flag), amendmentId, termLoanId, panel (notes | flags | tasks | logs | news | intel) opens the side panel on that tab, taskId opens that task in the side panel, activityView (activity | ledger) with tab=Activity, activityId (a funding, wire or check id) previews that activity row, txType (debits | credits) with tab=Bank Transactions, application=open (with applicationTab background | team | financials) opens the submitted application',
    category: 'Clients',
  },
  CLIENT_PAGE_RISK_PROFILE: {
    label: 'Risk & compliance',
    description: 'Client page, Risk & Compliance tab',
    category: 'Clients',
  },
  CLIENT_PAGE_FINANCIALS_TAB: {
    label: 'Financials',
    description: 'Client page, Financials tab',
    category: 'Clients',
  },
  CLIENT_SUBMIT_REPORTING_PAGE: {
    label: 'Submit reporting',
    description: 'Client-facing submit-reporting flow',
    category: 'Clients',
  },
  CLIENT_SUBMIT_LEGAL_REPORTING_PAGE: {
    label: 'Submit legal reporting',
    description: 'Client-facing legal reporting submission',
    category: 'Clients',
  },
  RESET_PASSWORD: {
    label: 'Reset password',
    description: 'Reset password (params: hash from email)',
    category: 'Auth',
  },
  PASSWORD_RECOVERY: {
    label: 'Password recovery',
    description: 'Password recovery start',
    category: 'Auth',
  },
  PASSWORD_SETUP: {
    label: 'Password setup',
    description: 'Initial password setup (params: hash from email)',
    category: 'Auth',
  },
  CLIENT_SETUP_CREATE: {
    label: 'New client',
    description: 'Create new client (setup wizard start)',
    category: 'ClientSetup',
  },
  CLIENT_SETUP: {
    label: 'Client setup',
    description: 'Client setup wizard root for client :id',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_LOAN_STRUCTURE_ASSET_BASED: {
    label: 'Asset-based structure',
    description: 'Client setup: asset-based loan structure',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_LOAN_STRUCTURE_TERM: {
    label: 'Term loan structure',
    description: 'Client setup: term loan structure',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_LOAN_STRUCTURE_OVERADVANCE: {
    label: 'Overadvance structure',
    description: 'Client setup: overadvance structure',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_LOAN_STRUCTURE_PARTICIPANTS: {
    label: 'Loan participants',
    description: 'Client setup: loan participants',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_COLLATERAL_RECEIVABLE: {
    label: 'Receivables collateral',
    description: 'Client setup: receivables collateral',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_COLLATERAL_PAYABLE: {
    label: 'Payables collateral',
    description: 'Client setup: payables collateral',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_COLLATERAL_INVENTORY: {
    label: 'Inventory collateral',
    description: 'Client setup: inventory collateral',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_ITEMS_TO_MONITOR: {
    label: 'Items to monitor',
    description: 'Client setup: items to monitor',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_REPORTING: {
    label: 'Reporting requirements',
    description: 'Client setup: reporting requirements',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_BANK_ACCOUNTS: {
    label: 'Bank accounts',
    description: 'Client setup: bank accounts',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_USERS: {
    label: 'Client users',
    description:
      'Client setup: users for client. Query param: editUserId opens that user for editing',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_RELATIONSHIP_MANAGEMENT: {
    label: 'Relationship management',
    description: 'Client setup: relationship management',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_TERMINATION: {
    label: 'Termination',
    description: 'Client setup: termination configuration',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_GENERAL: {
    label: 'General',
    description: 'Client setup: general info',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_API_KEYS: {
    label: 'API keys',
    description: 'Client setup: API keys for external API access',
    category: 'ClientSetup',
  },
  CLIENT_SETUP_COMMUNICATIONS: {
    label: 'Communications',
    description:
      "Client setup: the client's Google Group email address and Twilio phone numbers used on retailer portals",
    category: 'ClientSetup',
  },
  CLIENT_SETUP_PHONE_NUMBERS: {
    label: 'Phone numbers',
    description: 'Legacy client setup phone numbers URL; redirects to Communications',
    category: 'ClientSetup',
  },
  EXTERNAL_API_DOCS: {
    label: 'API docs',
    description: 'External API documentation (standalone page)',
    category: 'Core',
  },
  CLIENT_INTEGRATIONS: {
    label: 'Integrations',
    description: 'Third-party integrations for a client (Codat, etc.)',
    category: 'ClientSetup',
  },
  CLIENT_INTAKE_HOME: {
    label: 'Client intake',
    description: "Prospect portal home — redirects to the signed-in prospect's own intake",
    category: 'ClientSetup',
  },
  CLIENT_INTAKE_CLAIM: {
    label: 'Claim intake link',
    description: 'Claim a pre-login intake link and set up an account (params: salesforceId)',
    category: 'ClientSetup',
  },
  CLIENT_INTAKE: {
    label: 'Client intake',
    description: 'Client intake form (params: salesforceId)',
    category: 'ClientSetup',
  },
  ERROR_CENTER: {
    label: 'Error Center',
    description:
      'Admin: system errors and failure alerts, with triage, resolve and Arc investigation. Query params: id (an error_triage id) opens that error in the detail drawer; tab (state | live)',
    category: 'Settings',
  },
  MY_FEEDBACK: {
    label: 'Feedback',
    description: "Feedback page — all submitted feedback + status, the current user's by default",
    category: 'Settings',
  },
  SETTINGS: {
    label: 'Settings',
    description: 'User account settings. Query param: tab picks the settings tab',
    category: 'Settings',
  },
  SETTINGS_PROFILE: { label: 'Profile', description: 'Settings: profile', category: 'Settings' },
  SETTINGS_SECURITY: {
    label: 'Security',
    description: 'Settings: security / password',
    category: 'Settings',
  },
  CLIENT_SETTINGS_NOTIFICATIONS: {
    label: 'Notification settings',
    description: 'Settings: notifications',
    category: 'Settings',
  },
  SETTINGS_DISPLAY: {
    label: 'Display',
    description: 'Settings: display preferences',
    category: 'Settings',
  },
  SETTINGS_ACCESS_TOKENS: {
    label: 'Access tokens',
    description: 'Settings: personal access tokens',
    category: 'Settings',
  },
  PORTFOLIO_SETTINGS: {
    label: 'Portfolio settings',
    description: 'Portfolio settings (internal) — client banner management',
    category: 'Settings',
  },
  CLIENT_MANAGEMENT: {
    label: 'Client management',
    description: 'Internal client management dashboard',
    category: 'ClientManagement',
  },
  CLIENT_REPORTING: {
    label: 'Compliance',
    description: 'Client management, Compliance tab',
    category: 'ClientManagement',
  },
  USER_MANAGEMENT: {
    label: 'User management',
    description: "User admin / management. Query param: teamsUserId opens that user's teams",
    category: 'ClientManagement',
  },
  TEAMS: {
    label: 'Teams',
    description: 'Teams admin (named groups of users that can be @-mentioned in AIM and Tasks)',
    category: 'ClientManagement',
  },
  AUDIT: { label: 'Audit log', description: 'Audit log', category: 'ClientManagement' },
  ACCOUNT_ACTIVITY: {
    label: 'Account activity',
    description: 'Account activity / loan ledger',
    category: 'AccountActivity',
  },
  SUBMIT_REPORTING: {
    label: 'Submit reporting',
    description: 'Submit reporting (internal)',
    category: 'Reporting',
  },
  ACTIVITY_QUEUE: {
    label: 'Operations',
    description: 'Operations / activity queue',
    category: 'Operations',
  },
  ANALYSIS_QUEUE: {
    label: 'Reporting queue',
    description: 'Operations, Reporting analysis tab',
    category: 'Operations',
  },
  OPERATIONS_AMENDMENTS: {
    label: 'Amendments',
    description: 'Operations, Amendments tab — portfolio-wide amendment analyses',
    category: 'Operations',
  },
  AMENDMENT_ANALYSIS: {
    label: 'Amendment analysis',
    description: 'Amendment analysis page (params: id, analysisId)',
    category: 'Clients',
  },
  OUTGOING_WIRE: {
    label: 'Outgoing wires',
    description: 'Outgoing wire processing',
    category: 'Operations',
  },
  COLLECTIONS_DASHBOARD: {
    label: 'Collections',
    description: 'Collections dashboard',
    category: 'Collections',
  },
  COLLECTIONS_DASHBOARD_OVERALL: {
    label: 'Collections overall',
    description: 'Collections dashboard, overall view',
    category: 'Collections',
  },
  COLLECTIONS_DASHBOARD_NON_CLIENT_RELATED_CASH: {
    label: 'Non-client cash',
    description: 'Collections dashboard: non-client-related cash',
    category: 'Collections',
  },
  COLLECTIONS_WIRES: {
    label: 'Wires',
    description: 'Collections: wire processing',
    category: 'Collections',
  },
  COLLECTIONS_MID_DAY_WIRES: {
    label: 'Mid-day wires',
    description: 'Collections: mid-day wires',
    category: 'Collections',
  },
  COLLECTIONS_CHECKS: {
    label: 'Checks',
    description: 'Collections: check processing',
    category: 'Collections',
  },
  COLLECTIONS_CHECK_INVOICE_REVIEW: {
    label: 'Check review',
    description:
      'Collections: checks whose remittance invoices need attention, across all batches or one (query: batchActivityId, optional)',
    category: 'Collections',
  },
  ENTITY_MANAGEMENT: {
    label: 'Entities',
    description: 'Entity management (Atlas-tracked legal entities)',
    category: 'Entities',
  },
  ORGANIZATION_PAGE: {
    label: 'Organization',
    description:
      'Unified organization profile — Overview plus Customer/Vendor/Investor tabs when a linked entity exists',
    category: 'Entities',
  },
  CRM_HOME: {
    label: 'CRM',
    description: 'CRM home. Query param: tab (Organizations | Companies | People | Campaigns)',
    category: 'Entities',
  },
  CRM_SETTINGS: {
    label: 'Mention review',
    description: 'CRM mention review — review and correct mentions extracted into the CRM',
    category: 'Entities',
  },
  CRM_ORGANIZATION_PAGE: {
    label: 'Organization',
    description: 'CRM organization profile page',
    category: 'Entities',
  },
  CRM_COMPANY_PAGE: {
    label: 'Company',
    description: 'CRM company profile page',
    category: 'Entities',
  },
  CRM_PERSON_PAGE: {
    label: 'Person',
    description: 'CRM person profile page',
    category: 'Entities',
  },
  CRM_CAMPAIGN_PAGE: {
    label: 'Campaign',
    description:
      'CRM campaign page — plan, members and their statuses, spend, referrals and deals, post-event review',
    category: 'Entities',
  },
  ENTITY_SETTINGS: {
    label: 'Entity settings',
    description: 'Entity settings root',
    category: 'Entities',
  },
  ENTITY_SETTINGS_GENERAL: {
    label: 'Entity general',
    description: 'Entity settings: general',
    category: 'Entities',
  },
  ENTITY_SETTINGS_RECEIVABLES: {
    label: 'Entity receivables',
    description: 'Entity settings: receivables',
    category: 'Entities',
  },
  ENTITY_SETTINGS_PAYABLES: {
    label: 'Entity payables',
    description: 'Entity settings: payables',
    category: 'Entities',
  },
  ENTITY_SETTINGS_ALIAS_MAPPING: {
    label: 'Alias mapping',
    description: 'Entity settings: alias mapping',
    category: 'Entities',
  },
  ENTITY_SETTINGS_RELATED_ENTITIES: {
    label: 'Related entities',
    description: 'Entity settings: related entities',
    category: 'Entities',
  },
  BBC_INVENTORY: {
    label: 'BBC inventory',
    description: 'Borrowing base certificate (BBC): inventory detail',
    category: 'BBC',
  },
  BBC_COLLATERAL_ELIGIBILITY: {
    label: 'Collateral eligibility',
    description: 'BBC: collateral eligibility',
    category: 'BBC',
  },
  BBC_COLLATERAL_ELIGIBILITY_RECEIVABLES: {
    label: 'Receivables eligibility',
    description: 'BBC: receivables eligibility',
    category: 'BBC',
  },
  BBC_COLLATERAL_ELIGIBILITY_INVENTORY: {
    label: 'Inventory eligibility',
    description: 'BBC: inventory eligibility',
    category: 'BBC',
  },
  BBC_AR_INELIGIBILITY: {
    label: 'AR ineligibility',
    description: 'BBC: AR ineligibility breakdown',
    category: 'BBC',
  },
  BBC_INVENTORY_INELIGIBLE: {
    label: 'Inventory ineligible',
    description: 'BBC: inventory ineligible items',
    category: 'BBC',
  },
  BBC_INVENTORY_INELIGIBLE_ANALYSIS: {
    label: 'Inventory ineligibility analysis',
    description: 'BBC: inventory ineligibility analysis',
    category: 'BBC',
  },
  BBC_ANALYSIS: { label: 'BBC analysis', description: 'BBC analysis page', category: 'BBC' },
  BBC_SUMMARY: { label: 'BBC summary', description: 'BBC summary (top-level)', category: 'BBC' },
  BBC_SUMMARY_HISTORICAL: {
    label: 'Historical summary',
    description: 'BBC summary, historical view',
    category: 'BBC',
  },
  BBC_SUMMARY_RECEIVABLES: {
    label: 'Receivables summary',
    description: 'BBC summary: receivables',
    category: 'BBC',
  },
  BBC_SUMMARY_PAYABLES: {
    label: 'Payables summary',
    description: 'BBC summary: payables',
    category: 'BBC',
  },
  BBC_SUMMARY_INVENTORY: {
    label: 'Inventory summary',
    description: 'BBC summary: inventory',
    category: 'BBC',
  },
  ONGOING_REPORTING: {
    label: 'Ongoing reporting',
    description: 'Ongoing reporting page',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_INCOME_STATEMENT: {
    label: 'Income statement',
    description: 'Ongoing reporting: income statement',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_BALANCE_SHEET: {
    label: 'Balance sheet',
    description: 'Ongoing reporting: balance sheet',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_SUMMARY: {
    label: 'Reporting summary',
    description: 'Ongoing reporting: summary',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_SALES_BY_SKU: {
    label: 'Sales by SKU',
    description: 'Ongoing reporting: sales by SKU',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_AR_GENERAL_LEDGER: {
    label: 'AR general ledger',
    description: 'Ongoing reporting: AR general ledger',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD: {
    label: 'Bank transactions',
    description: 'Ongoing reporting: bank transactions upload',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_CAP_TABLE_UPLOAD: {
    label: 'Cap table',
    description: 'Ongoing reporting: cap table upload',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_INSURANCE_PAGE: {
    label: 'Insurance',
    description: 'Ongoing reporting: insurance certificates',
    category: 'OngoingReporting',
  },
  ONGOING_REPORTING_CERTIFICATES_PAGE: {
    label: 'Certificates',
    description: 'Ongoing reporting: certificates',
    category: 'OngoingReporting',
  },
  PROSPECTS: {
    label: 'Prospects',
    description:
      'Prospects list (sales pipeline). Query param: tab (Pipeline | Qualifying | Radar | Analysis Queue)',
    category: 'Prospects',
  },
  PROSPECT_SETUP: {
    label: 'Prospect setup',
    description: 'Prospect setup root',
    category: 'Prospects',
  },
  PROSPECT_PAGE: { label: 'Prospect', description: 'Prospect overview', category: 'Prospects' },
  PROSPECT_OVERVIEW_PAGE: {
    label: 'Prospect overview',
    description:
      'Prospect: CRM Overview tab — relationship-first company snapshot. Query param: application=open opens the submitted application',
    category: 'Prospects',
  },
  PROSPECT_APPLICATION_PAGE: {
    label: 'Application',
    description: 'Prospect: application',
    category: 'Prospects',
  },
  PROSPECT_ANALYSIS_QUEUE: {
    label: 'Analysis queue',
    description: 'Prospect: analysis queue',
    category: 'Prospects',
  },
  PROSPECT_SUMMARY_PAGE: {
    label: 'Prospect summary',
    description: 'Prospect: summary',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_RECEIVABLES: {
    label: 'Receivables',
    description: 'Prospect reporting: receivables',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_PAYABLES: {
    label: 'Payables',
    description: 'Prospect reporting: payables',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_INVENTORY: {
    label: 'Inventory',
    description: 'Prospect reporting: inventory',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_INCOME_STATEMENT: {
    label: 'Income statement',
    description: 'Prospect reporting: income statement',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_BALANCE_SHEET: {
    label: 'Balance sheet',
    description: 'Prospect reporting: balance sheet',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_INCOME_STATEMENT_PROJECTIONS: {
    label: 'Income statement projections',
    description: 'Prospect reporting: income statement projections',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_BALANCE_SHEET_PROJECTIONS: {
    label: 'Balance sheet projections',
    description: 'Prospect reporting: balance sheet projections',
    category: 'Prospects',
  },
  PROSPECT_REPORTING_CAP_TABLE: {
    label: 'Cap table',
    description: 'Prospect reporting: detailed cap table',
    category: 'Prospects',
  },
  PROSPECT_SETTINGS_USERS_PAGE: {
    label: 'Portal users',
    description:
      'Prospect: settings — portal users. Query param: editUserId opens that user for editing',
    category: 'Prospects',
  },
  PROSPECT_MANAGE_TERMS_PAGE: {
    label: 'Manage terms',
    description: 'Prospect: manage terms',
    category: 'Prospects',
  },
  PROSPECT_ADD_TERMS_PAGE: {
    label: 'Add term',
    description: 'Prospect: add term',
    category: 'Prospects',
  },
  PROSPECT_EDIT_TERMS_PAGE: {
    label: 'Edit term',
    description: 'Prospect: edit term (params: id, termId)',
    category: 'Prospects',
  },
  DUE_DILIGENCE_HOME: {
    label: 'Due diligence',
    description: 'Due diligence home / queue',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_SETTINGS: {
    label: 'Due diligence settings',
    description: 'Due diligence settings',
    category: 'DueDiligence',
  },
  AMENDMENT_ANALYSIS_SETTINGS: {
    label: 'Amendment analysis settings',
    description: 'Amendment analysis settings (default commentary templates)',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE: {
    label: 'Due diligence',
    description:
      'Due diligence overview. Query params: application=open (with applicationTab background | team | financials) opens the submitted application, panel (notes | flags | tasks) opens the side panel on that tab, noteId opens that note in the side panel',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_OVERVIEW: {
    label: 'Company overview',
    description:
      'Due diligence company overview (CRM). Query params: application=open (with applicationTab background | team | financials) opens the submitted application',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_CLIENT_FINANCIALS_PAGE: {
    label: 'Client financials',
    description: 'DD: client financials',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_ANALYSIS_QUEUE: {
    label: 'Analysis queue',
    description: 'DD: analysis queue',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_LIST: {
    label: 'Process documents',
    description: 'DD: process documents list (params: id, type)',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_MAPPING: {
    label: 'Document mapping',
    description: 'DD: process documents mapping (params: id, type)',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_FINANCIALS: {
    label: 'Financials',
    description: 'DD: financials process page (params: clientId, type, id)',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_BANK_TRANSACTIONS: {
    label: 'Transaction mapping',
    description: 'DD: bank transactions mapping',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_GENERAL_LEDGER: {
    label: 'AR ledger mapping',
    description: 'DD: AR general ledger mapping',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_INVENTORY: {
    label: 'Inventory mapping',
    description: 'DD: inventory mapping',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_SALES_BY_SKU: {
    label: 'Sales by SKU mapping',
    description: 'DD: sales by SKU mapping',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_PROCESS_DOCUMENTS_CAP_TABLE: {
    label: 'Cap table mapping',
    description: 'DD: cap table mapping',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE: {
    label: 'Validate documents',
    description: 'DD: validate documents',
    category: 'DueDiligence',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAGE: {
    label: 'Loan committee report',
    description: 'Loan committee report (LCR) overview',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FINANCIALS_PAGE: {
    label: 'LCR financials',
    description: 'LCR: financials',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FORECAST_PAGE: {
    label: 'LCR forecasts',
    description: 'LCR: forecasts',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_TRANSACTIONS_PAGE: {
    label: 'LCR transactions',
    description: 'LCR: transactions',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_RECEIVABLES_PAGE: {
    label: 'LCR receivables',
    description: 'LCR: receivables',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAYABLES_PAGE: {
    label: 'LCR payables',
    description: 'LCR: payables',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_INVENTORY_PAGE: {
    label: 'LCR inventory',
    description: 'LCR: inventory',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_CAP_TABLE_PAGE: {
    label: 'LCR cap table',
    description: 'LCR: cap table',
    category: 'LoanCommittee',
  },
  DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FIELD_EXAM_PAGE: {
    label: 'LCR field exam',
    description: 'LCR: field exam',
    category: 'LoanCommittee',
  },
  ENTITY_PAGE: { label: 'Entity', description: 'Entity profile page', category: 'Entities' },
  PARTICIPANTS_MANAGEMENT: {
    label: 'Participants',
    description: 'Participants management (loan syndication partners)',
    category: 'Participants',
  },
  PARTICIPANTS_PAGE: {
    label: 'Participant',
    description: 'Participant detail page',
    category: 'Participants',
  },
  PARTICIPANT_SETTINGS: {
    label: 'Participant settings',
    description: 'Participant settings',
    category: 'Participants',
  },
  MARKET_NEWS: { label: 'Market news', description: 'Market news feed', category: 'News' },
  MARKET_INTELLIGENCE: {
    label: 'Market intelligence',
    description: 'Portfolio-wide market intelligence captured by RMs (internal only)',
    category: 'News',
  },
  NEWSFEED_WRONG_MATCH: {
    label: 'Newsfeed correction',
    description: 'Newsfeed wrong-match correction (params: newsfeedId)',
    category: 'News',
  },
  NOTIFICATIONS: {
    label: 'Notifications',
    description: 'User notifications inbox',
    category: 'Core',
  },
  FUNDING: { label: 'Funding', description: 'Funding requests', category: 'Operations' },
  EXPORTS_PAGE: { label: 'Exports', description: 'Exports / downloads', category: 'Operations' },
  REPORTS: {
    label: 'Reports',
    description: "The caller's saved reports (Report Builder)",
    category: 'Reports',
  },
  REPORT_PAGE: {
    label: 'Report',
    description:
      'A saved report: re-runs its filters, columns and sort on open (params: id — the report id)',
    category: 'Reports',
  },
  DOCUMENT_EDITOR: {
    label: 'Document editor',
    description: 'DD document editor (params: id, fileId)',
    category: 'DueDiligence',
  },
  CODAT_REDIRECT_PAGE: {
    label: 'Connecting accounting',
    description: 'Codat OAuth redirect handler (params: companyId)',
    category: 'ClientSetup',
  },
  QBO_REDIRECT_PAGE: {
    label: 'Connecting QuickBooks',
    description: 'Direct QuickBooks Online OAuth redirect handler',
    category: 'ClientSetup',
  },
  GLOBAL_SEARCH: { label: 'Search', description: 'Global search results', category: 'Core' },
  GLOBAL_ERROR: { label: 'Error', description: 'Global error page', category: 'Core' },
  CLIENT_VISUALIZATIONS: {
    label: 'Analytics',
    description: 'Client visualizations',
    category: 'Visualizations',
  },
  APPLICATION_PAGE: {
    label: 'Application',
    description: 'Application page (client-facing)',
    category: 'ClientFacing',
  },
  DOCUMENTS_PAGE: {
    label: 'Documents',
    description: 'Documents page (client-facing)',
    category: 'ClientFacing',
  },
}

/**
 * Extract `:param` placeholder names from a route template.
 * Returns the unique param names in order of first appearance.
 */
export function extractRouteParams(pathTemplate: string): string[] {
  const seen = new Set<string>()
  const params: string[] = []
  const regex = /:([a-zA-Z][a-zA-Z0-9]*)/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(pathTemplate)) !== null) {
    if (!seen.has(match[1])) {
      seen.add(match[1])
      params.push(match[1])
    }
  }
  return params
}

/**
 * Route patterns ordered longest-first, so the most specific one wins:
 * `/bbc/:id/summary/receivables` has to be tried before `/bbc/:id`. Built once —
 * the table is static, and this runs on every navigation.
 *
 * The two `?tab=` entries are deep links into another route's tabs rather than
 * patterns of their own, so they are left out of matching.
 */
const MATCHABLE_ROUTES: Array<{ pattern: string; key: RouteKey }> = (
  Object.keys(ROUTES) as RouteKey[]
)
  .filter((key) => !ROUTES[key].includes('?'))
  .map((key) => ({ pattern: ROUTES[key], key }))
  .sort((a, b) => {
    const bySegments = b.pattern.split('/').length - a.pattern.split('/').length
    // At equal depth a literal segment beats a `:param`, so `/ongoing-reporting/sales-by-sku/:id`
    // is not swallowed by `/ongoing-reporting/:id/summary`.
    return bySegments || extractRouteParams(a.pattern).length - extractRouteParams(b.pattern).length
  })

/** Turns one `/:param` segment into a matcher for a single non-empty segment. */
const toPatternRegExp = (pattern: string): RegExp =>
  new RegExp(`^${escapeRegExp(pattern).replace(/\/:[a-zA-Z][a-zA-Z0-9]*/g, '/([^/]+)')}/?$`)

const ROUTE_MATCHERS: Array<{ regex: RegExp; key: RouteKey }> = MATCHABLE_ROUTES.map(
  ({ pattern, key }) => ({ regex: toPatternRegExp(pattern), key }),
)

/**
 * The short human name for whatever route a pathname lands on, or undefined when
 * it matches none. Lets any page be named in the UI without the page itself
 * having to opt in.
 */
export function getRouteLabel(pathname: string): string | undefined {
  return getRouteMetadata(pathname)?.label
}

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/** The route a pathname lands on, with the value of each of its `:params`. */
export function matchRoute(
  pathname: string,
): { key: RouteKey; params: Record<string, string> } | undefined {
  for (const { regex, key } of ROUTE_MATCHERS) {
    const match = regex.exec(pathname)
    if (!match) continue
    const names = extractRouteParams(ROUTES[key])
    return {
      key,
      params: Object.fromEntries(names.map((name, i) => [name, decodeSegment(match[i + 1])])),
    }
  }
  return undefined
}

/** Everything known about the route a pathname lands on: its name, what it shows, its area. */
export function getRouteMetadata(pathname: string): IRouteMetadata | undefined {
  const match = matchRoute(pathname)
  return match && ROUTE_METADATA[match.key]
}
